/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { BaseButton, ConditionalWrapper, Icon, Spacer } from "@cd2/components"
import { icons } from "../../constants/icons"
import { brandColors } from "../EOSThemeProvider"
import { Form2 } from "@cd2/forms/build/es6"
import { titleize } from "@cd2/tools/build/es6"
import { P } from "@cd2/text/build/es6"
import { QuickToolTip } from "../QuickTooltip"

interface ICheckboxProps {
  label: string
  description?: React.ReactNode
  value: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
  tooltip?: string
}

export function Checkbox({
  label,
  description,
  value,
  onChange,
  disabled,
  tooltip,
}: ICheckboxProps) {
  return (
    <ConditionalWrapper
      condition={!!tooltip}
      Wrapper={QuickToolTip}
      wrapperProps={{ content: tooltip }}
    >
      <BaseButton
        theme={{
          weight: 500,
          padding: { topAndBottom: description ? 8 : 12, sides: 8 },
          borderColor: brandColors.midGrey,
          color: brandColors.night,
        }}
        renderLeft={() => (
          <div
            css={css`
              width: 18px;
              height: 18px;
              border: 1px solid ${brandColors.lighterNight};
              transition: all 0.2s ease-in-out;
              background: ${value ? brandColors.mint : "white"};
              border-radius: 4px;
              padding: 1px 2px;
            `}
          >
            {value && <Icon icon={icons.check} color={brandColors.night} />}
          </div>
        )}
        buttonStyle="text"
        onClick={disabled ? undefined : () => onChange(!value)}
        centralButtonClassName="center-cn"
        css={css`
          .center-cn {
            margin: 0;
          }

          opacity: 1;
        `}
        disabled={disabled}
        block
      >
        <div
          css={css`
            text-align: left;
          `}
        >
          {label ?? ""}
          {description && (
            <div>
              <Spacer size={2} />
              {typeof description === "string" ? (
                <P size="small" color={brandColors.lightNight}>
                  {description}
                </P>
              ) : (
                description
              )}
            </div>
          )}
        </div>
      </BaseButton>
    </ConditionalWrapper>
  )
}

interface IFormCheckboxProps {
  field: string
  label?: string
  description?: React.ReactNode
  disabled?: boolean
  tooltip?: string
}

function Form({ field: fieldName, label, description, disabled, tooltip }: IFormCheckboxProps) {
  return (
    <Form2.WithField<boolean>
      field={fieldName}
      render={field => (
        <Checkbox
          label={label || titleize(fieldName)}
          value={field.getValue()}
          onChange={disabled ? () => {} : field.setValue}
          description={description}
          disabled={disabled}
          tooltip={tooltip}
        />
      )}
    />
  )
}

Checkbox.Form = Form
